 
/* eslint-disable @typescript-eslint/no-unused-vars */
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { createSyncMiddleware, WithSyncControls } from './middleware/syncMiddleware';

export type NotificationType = 'info' | 'success' | 'warning' | 'error';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  timestamp: Date;
  duration?: number; // milliseconds, undefined = persistent
  read?: boolean;
}

export interface DebugPanelState {
  salesDebugger: boolean;
  competitorDebugger: boolean;
  eventDebugger: boolean;
  productDebugger: boolean;
  performanceMonitor: boolean;
  gameStateViewer: boolean;
}

export interface SalesDebugState {
  isOpen: boolean;
  selectedProductId: string | null;
  showDetailedMetrics: boolean;
  showHistoricalData: boolean;
  refreshInterval: number; // milliseconds
}

interface UIStore extends WithSyncControls {
  // State
  activeModal: string | null;
  selectedTab: string;
  notifications: Notification[];
  debugPanels: DebugPanelState;
  salesDebugPanel: SalesDebugState;
  sidebarCollapsed: boolean;
  tutorialStep: number | null;
  tutorialCompleted: boolean;
  tutorialSkipped: boolean;
  tooltipTarget: string | null;

  // Actions
  openModal: (modal: string) => void;
  closeModal: () => void;
  setActiveTab: (tab: string) => void;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => void;
  removeNotification: (id: string) => void;
  markNotificationRead: (id: string) => void;
  clearNotifications: () => void;
  toggleDebugPanel: (panel: keyof DebugPanelState) => void;
  updateSalesDebugState: (state: Partial<SalesDebugState>) => void;
  toggleSidebar: () => void;
  setTutorialStep: (step: number | null) => void;
  setTutorialCompleted: (completed: boolean) => void;
  setTutorialSkipped: (skipped: boolean) => void;
  setTooltipTarget: (target: string | null) => void;
  resetUI: () => void;
}

const defaultDebugPanels: DebugPanelState = {
  salesDebugger: false,
  competitorDebugger: false,
  eventDebugger: false,
  productDebugger: false,
  performanceMonitor: false,
  gameStateViewer: false,
};

const defaultSalesDebugState: SalesDebugState = {
  isOpen: false,
  selectedProductId: null,
  showDetailedMetrics: true,
  showHistoricalData: false,
  refreshInterval: 1000,
};

// Partialize function for selective persistence
const partializeUIStore = (state: UIStore) => {
  // Only persist user preferences and layout settings
  // Don't persist modals, notifications, or temporary state
  const {
    // Exclude temporary UI state
    activeModal: _activeModal,
    notifications: _notifications,
    tooltipTarget: _tooltipTarget,
    // Exclude action functions
    openModal: _openModal,
    closeModal: _closeModal,
    setActiveTab: _setActiveTab,
    addNotification: _addNotification,
    removeNotification: _removeNotification,
    markNotificationRead: _markNotificationRead,
    clearNotifications: _clearNotifications,
    toggleDebugPanel: _toggleDebugPanel,
    updateSalesDebugState: _updateSalesDebugState,
    toggleSidebar: _toggleSidebar,
    setTutorialStep: _setTutorialStep,
    setTooltipTarget: _setTooltipTarget,
    resetUI: _resetUI,
    sync: _sync,
    ...persistableState
  } = state;
  
  return {
    selectedTab: persistableState.selectedTab,
    debugPanels: persistableState.debugPanels,
    salesDebugPanel: {
      ...persistableState.salesDebugPanel,
      isOpen: false // Don't persist open state
    },
    sidebarCollapsed: persistableState.sidebarCollapsed,
    tutorialStep: persistableState.tutorialStep,
    tutorialCompleted: persistableState.tutorialCompleted,
    tutorialSkipped: persistableState.tutorialSkipped
  };
};

// Sync configuration for UI store
const uiSyncConfig = {
  routing: {
    rules: [
      // All UI preferences stay in localStorage for fast restoration
      {
        match: () => true,
        target: 'localStorage' as const,
        priority: 100
      }
    ]
  },
  behavior: {
    debounceDelay: 0, // No debounce for preference changes
    batchSize: 10,
    enableAutoSync: true,
    syncOnReconnect: false // UI state doesn't need reconnect sync
  },
  hooks: {
    beforeSync: (data) => {
      // Ensure minimal persistence footprint
      return partializeUIStore(data);
    },
    afterSync: (result) => {
      if (result.success) {
        // console.debug('[UIStore] Preferences saved');
      }
    },
    onError: (error) => {
      // UI sync errors are non-critical
      console.warn('[UIStore] Sync error:', error.message);
    }
  },
  monitoring: {
    trackMetrics: false, // Minimal monitoring for UI store
    reportInterval: 300000 // Report every 5 minutes if enabled
  }
};

export const useUIStore = create<UIStore>()(
  devtools(
    createSyncMiddleware(
      'uiStore',
      uiSyncConfig
    )(
      (set) => ({
      // Initial state
      activeModal: null,
      selectedTab: 'overview',
      notifications: [],
      debugPanels: defaultDebugPanels,
      salesDebugPanel: defaultSalesDebugState,
      sidebarCollapsed: false,
      tutorialStep: null,
      tutorialCompleted: false,
      tutorialSkipped: false,
      tooltipTarget: null,

      // Actions
      openModal: (modal) =>
        set({ activeModal: modal }, false, 'openModal'),

      closeModal: () =>
        set({ activeModal: null }, false, 'closeModal'),

      setActiveTab: (tab) =>
        set({ selectedTab: tab }, false, 'setActiveTab'),

      addNotification: (notification) =>
        set(
          (state) => ({
            notifications: [
              ...state.notifications,
              {
                ...notification,
                id: `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                timestamp: new Date(),
                read: false,
              },
            ],
          }),
          false,
          'addNotification'
        ),

      removeNotification: (id) =>
        set(
          (state) => ({
            notifications: state.notifications.filter((n) => n.id !== id),
          }),
          false,
          'removeNotification'
        ),

      markNotificationRead: (id) =>
        set(
          (state) => ({
            notifications: state.notifications.map((n) =>
              n.id === id ? { ...n, read: true } : n
            ),
          }),
          false,
          'markNotificationRead'
        ),

      clearNotifications: () =>
        set({ notifications: [] }, false, 'clearNotifications'),

      toggleDebugPanel: (panel) =>
        set(
          (state) => ({
            debugPanels: {
              ...state.debugPanels,
              [panel]: !state.debugPanels[panel],
            },
          }),
          false,
          `toggleDebugPanel:${panel}`
        ),

      updateSalesDebugState: (salesState) =>
        set(
          (state) => ({
            salesDebugPanel: {
              ...state.salesDebugPanel,
              ...salesState,
            },
          }),
          false,
          'updateSalesDebugState'
        ),

      toggleSidebar: () =>
        set(
          (state) => ({ sidebarCollapsed: !state.sidebarCollapsed }),
          false,
          'toggleSidebar'
        ),

      setTutorialStep: (step) =>
        set({ tutorialStep: step }, false, 'setTutorialStep'),

      setTutorialCompleted: (completed) =>
        set({ tutorialCompleted: completed }, false, 'setTutorialCompleted'),

      setTutorialSkipped: (skipped) =>
        set({ tutorialSkipped: skipped }, false, 'setTutorialSkipped'),

      setTooltipTarget: (target) =>
        set({ tooltipTarget: target }, false, 'setTooltipTarget'),

      resetUI: () =>
        set(
          {
            activeModal: null,
            selectedTab: 'overview',
            notifications: [],
            debugPanels: defaultDebugPanels,
            salesDebugPanel: defaultSalesDebugState,
            sidebarCollapsed: false,
            tutorialStep: null,
            tutorialCompleted: false,
            tutorialSkipped: false,
            tooltipTarget: null,
          },
          false,
          'resetUI'
        ),
      }),
    ),
    {
      name: 'ui-store',
    }
  )
);

// Selectors
export const selectActiveModal = (state: UIStore) => state.activeModal;
export const selectSelectedTab = (state: UIStore) => state.selectedTab;
export const selectNotifications = (state: UIStore) => state.notifications;
export const selectDebugPanels = (state: UIStore) => state.debugPanels;
export const selectSalesDebugPanel = (state: UIStore) => state.salesDebugPanel;

// Computed selectors
export const selectUnreadNotificationCount = (state: UIStore) =>
  state.notifications.filter((n) => !n.read).length;

export const selectActiveNotifications = (state: UIStore) =>
  state.notifications.filter((n) => {
    if (!n.duration) return true; // Persistent notifications
    const elapsed = Date.now() - n.timestamp.getTime();
    return elapsed < n.duration;
  });

export const selectIsAnyDebugPanelOpen = (state: UIStore) => {
  const panels = state.debugPanels;
  return (
    panels.salesDebugger ||
    panels.competitorDebugger ||
    panels.eventDebugger ||
    panels.productDebugger ||
    panels.performanceMonitor ||
    panels.gameStateViewer
  );
};

export const selectNotificationsByType = (type: NotificationType) => (state: UIStore) =>
  state.notifications.filter((n) => n.type === type);